import { useState, useEffect } from 'react';
import { Search, Loader2, User, Car, PoundSterling } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
    id: string;
    type: 'customer' | 'vehicle';
    title: string;
    subtitle: string;
    metadata: any;
}

export default function GlobalSearch({ className }: { className?: string }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();

    // Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query);
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    // Search Effect
    useEffect(() => {
        const search = async () => {
            if (!debouncedQuery || debouncedQuery.length < 2) {
                setResults([]);
                return;
            }

            if (!activeOrgId) {
                console.warn('GlobalSearch: No activeOrgId found');
                return;
            }

            setLoading(true);
            try {
                const { data, error } = await supabase
                    .rpc('search_crm', {
                        search_term: debouncedQuery,
                        org_id: activeOrgId
                    });

                if (error) throw error;
                setResults(data || []);
            } catch (err) {
                console.error('Search failed:', err);
            } finally {
                setLoading(false);
            }
        };

        search();
    }, [debouncedQuery, activeOrgId]);

    const handleSelect = (result: SearchResult) => {
        if (result.type === 'customer') {
            navigate(`/clients/${result.id}`);
        } else if (result.type === 'vehicle') {
            // If vehicle, navigate to owner's profile but maybe highlight vehicle?
            // For now, assume metadata contains owner_id
            if (result.metadata?.owner_id) {
                navigate(`/clients/${result.metadata.owner_id}`);
            }
        }
    };

    return (
        <div className={`relative max-w-2xl mx-auto w-full ${className}`}>
            <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-chart-4/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-panel border border-line rounded-xl flex items-center px-4 py-3 shadow-2xl overflow-hidden focus-within:ring-2 ring-accent/50 transition-all">
                    <Search className="w-5 h-5 text-fg-3 mr-3" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search customers, phones, or reg plates..."
                        className="bg-transparent border-none outline-none text-lg text-fg placeholder-fg-4 w-full font-medium"
                        autoFocus
                    />
                    {loading && <Loader2 className="w-5 h-5 text-accent animate-spin" />}
                </div>
            </div>

            {/* Results Dropdown */}
            {results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-4 bg-panel border border-line rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="py-2">
                        {results.map((result) => (
                            <button
                                key={`${result.type}-${result.id}`}
                                onClick={() => handleSelect(result)}
                                className="w-full text-left px-4 py-3 hover:bg-raised flex items-center gap-3 sm:gap-4 transition-colors border-b border-tint/5 last:border-0"
                            >
                                <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${result.type === 'customer'
                                    ? 'bg-info/10 text-info'
                                    : 'bg-success/10 text-success'
                                    }`}>
                                    {result.type === 'customer' ? <User className="w-5 h-5" /> : <Car className="w-5 h-5" />}
                                </div>
                                {/* An email address is one unbreakable token, so
                                    without min-w-0 + truncate this column sets a
                                    ~200px floor and shoves the totals off a phone. */}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-fg font-medium truncate">{result.title}</h4>
                                    <p className="text-fg-4 text-sm truncate">{result.subtitle}</p>
                                    {result.type === 'customer' && result.metadata?.address && result.subtitle !== result.metadata.address && (
                                        <p className="text-fg-4 text-fd-3xs mt-0.5 line-clamp-1 italic">{result.metadata.address}</p>
                                    )}
                                </div>

                                {result.type === 'customer' && (result.metadata?.ltv !== undefined || result.metadata?.owed !== undefined) && (
                                    <div className="ml-auto shrink-0 text-right flex flex-col items-end gap-1">
                                        {result.metadata?.ltv > 0 && (
                                            <div className="flex items-center gap-2">
                                                <div className="text-fd-3xs text-fg-4 uppercase tracking-tighter">Paid</div>
                                                <span className="inline-flex items-center gap-1 font-mono text-success bg-success/10 px-1.5 py-0.5 rounded text-xs border border-success/20">
                                                    <PoundSterling className="w-2.5 h-2.5" />
                                                    {Number(result.metadata.ltv).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        )}
                                        {result.metadata?.owed > 0 && (
                                            <div className="flex items-center gap-2">
                                                <div className="text-fd-3xs text-fg-4 uppercase tracking-tighter">Owed</div>
                                                <span className="inline-flex items-center gap-1 font-mono text-gold bg-gold-fill/10 px-1.5 py-0.5 rounded text-xs border border-gold/20">
                                                    <PoundSterling className="w-2.5 h-2.5" />
                                                    {Number(result.metadata.owed).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {result.type === 'vehicle' && (
                                    <div className="ml-auto shrink-0">
                                        <span className="text-fd-3xs font-mono bg-tint/5 px-2 py-1 rounded text-fg-3 border border-tint/5">
                                            VEHICLE
                                        </span>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {query && results.length === 0 && !loading && (
                <div className="absolute top-full left-0 right-0 mt-4 text-center py-8 text-fg-4 bg-panel/50 rounded-xl border border-dashed border-tint/10 backdrop-blur-sm">
                    No results found for "{query}"
                </div>
            )}
        </div>
    );
}
